import { Link } from 'react-router-dom'
import type { Post } from '../../types'

interface TopPostsTableProps {
  posts: Post[]
  title?: string
}

const THEMES: Record<string, { grad: string; icon: string; sub: string }> = {
  'Top Posts by Views': {
    grad: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
    icon: '🏆',
    sub: 'Most viewed posts of all time',
  },
  'Recently Published': {
    grad: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
    icon: '🆕',
    sub: 'Latest content added to your blog',
  },
}

const RANK_LABELS = ['🥇', '🥈', '🥉']

function ViewBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div className="tpt-bar-track">
      <div className="tpt-bar-fill" style={{ width: `${pct}%` }} />
    </div>
  )
}

export function TopPostsTable({ posts, title = 'Top Posts by Views' }: TopPostsTableProps) {
  const theme = THEMES[title] ?? THEMES['Recently Published']
  const maxViews = posts[0]?.viewCount ?? 1
  const showViews = title === 'Top Posts by Views'

  return (
    <>
      <div className="tpt-card">
        <div className="tpt-head" style={{ background: theme.grad }}>
          <div className="tpt-head-left">
            <span className="tpt-head-icon">{theme.icon}</span>
            <div>
              <h3 className="tpt-head-title">{title}</h3>
              <p className="tpt-head-sub">{theme.sub}</p>
            </div>
          </div>
          <span className="tpt-head-count">{posts.length} posts</span>
        </div>

        <div className="tpt-body">
          {posts.length === 0 ? (
            <p className="tpt-empty">No posts yet.</p>
          ) : (
            <div className="tpt-table-wrap">
            <table className="tpt-table">
              <thead>
                <tr>
                  <th className="tpt-th tpt-th--rank">#</th>
                  <th className="tpt-th">Post</th>
                  {showViews && <th className="tpt-th tpt-th--bar">Trend</th>}
                  {showViews
                    ? <th className="tpt-th tpt-th--num">Views</th>
                    : <th className="tpt-th tpt-th--num">Read</th>
                  }
                </tr>
              </thead>
              <tbody>
                {posts.map((post, i) => (
                  <tr key={post.id} className={i % 2 === 0 ? 'tpt-tr-even' : ''}>
                    <td className="tpt-td tpt-td--rank">
                      {i < 3
                        ? <span className="tpt-medal">{RANK_LABELS[i]}</span>
                        : <span className="tpt-rank-num">#{i + 1}</span>
                      }
                    </td>
                    <td className="tpt-td">
                      <Link to={`/posts/${post.slug}/edit`} className="tpt-link">
                        {post.title}
                      </Link>
                      <div className="tpt-meta">
                        <span className="tpt-category">{post.category}</span>
                        {!showViews && post.publishedAt && (
                          <span className="tpt-date">
                            {new Date(post.publishedAt).toLocaleDateString(undefined, {
                              month: 'short', day: 'numeric', year: 'numeric',
                            })}
                          </span>
                        )}
                      </div>
                    </td>
                    {showViews && (
                      <td className="tpt-td tpt-td--bar">
                        <ViewBar value={post.viewCount} max={maxViews} />
                      </td>
                    )}
                    <td className="tpt-td tpt-td--num">
                      {showViews
                        ? post.viewCount.toLocaleString()
                        : `${post.readingTimeMin} min`
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .tpt-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .tpt-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          gap: 10px;
          flex-wrap: wrap;
        }
        .tpt-head-left  { display: flex; align-items: center; gap: 10px; min-width: 0; flex: 1; }
        .tpt-head-icon  { font-size: 1.3rem; line-height: 1; flex-shrink: 0; }
        .tpt-head-title { font-size: 0.88rem; font-weight: 700; color: #fff; margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tpt-head-sub   { font-size: 0.7rem; color: rgba(255,255,255,0.72); margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tpt-head-count {
          font-size: 0.72rem;
          font-weight: 600;
          color: rgba(255,255,255,0.9);
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.25);
          border-radius: 20px;
          padding: 3px 10px;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .tpt-body { padding: 0; }
        .tpt-empty {
          font-size: 0.875rem;
          color: var(--text-muted);
          padding: 24px 20px;
          margin: 0;
        }

        .tpt-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .tpt-table {
          width: 100%;
          min-width: 320px;
          border-collapse: collapse;
          font-size: 0.83rem;
        }
        .tpt-th {
          padding: 8px 10px;
          text-align: left;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #6366f1;
          background: rgba(99,102,241,0.06);
          border-bottom: 1px solid rgba(99,102,241,0.15);
          white-space: normal;
        }
        .tpt-th--rank { width: 36px; text-align: center; }
        .tpt-th--num  { text-align: right; width: 60px; }
        .tpt-th--bar  { width: 80px; }

        .tpt-td {
          padding: 10px 10px;
          vertical-align: middle;
          color: var(--text-secondary);
          border-bottom: 1px solid var(--border-light, var(--border));
        }
        .tpt-table tbody tr:last-child .tpt-td { border-bottom: none; }
        .tpt-td--rank { text-align: center; width: 36px; }
        .tpt-td--num  { text-align: right; font-weight: 700; color: var(--text-primary); font-variant-numeric: tabular-nums; white-space: nowrap; }
        .tpt-td--bar  { width: 80px; padding-right: 8px; }

        .tpt-tr-even { background: rgba(99,102,241,0.025); }
        .tpt-table tbody tr:hover { background: rgba(99,102,241,0.06); }

        .tpt-medal    { font-size: 1.1rem; line-height: 1; }
        .tpt-rank-num {
          font-size: 0.72rem;
          font-weight: 700;
          color: var(--text-muted);
        }

        .tpt-link {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 0.84rem;
          line-height: 1.35;
          text-decoration: none;
          transition: color 0.12s;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: min(220px, 40vw);
        }
        .tpt-link:hover { color: #6366f1; }

        .tpt-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 2px;
        }
        .tpt-category {
          font-size: 0.7rem;
          color: var(--text-muted);
          text-transform: capitalize;
        }
        .tpt-date {
          font-size: 0.7rem;
          color: var(--text-muted);
        }
        .tpt-date::before { content: '·'; margin-right: 8px; }

        .tpt-bar-track {
          width: 100%;
          height: 5px;
          background: rgba(99,102,241,0.12);
          border-radius: 3px;
          overflow: hidden;
        }
        .tpt-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #4338ca, #818cf8);
          border-radius: 3px;
          transition: width 0.3s ease;
          min-width: 2px;
        }
      `}</style>
    </>
  )
}
